import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { GlobalConstants } from 'src/constants/global.constants';
import { environment } from 'src/environments/environment';
import { GroupBy } from '../_enums/groupby';
import { ResourceTypeCodes } from '../_enums/resource.type.codes';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { Atelier } from '../_models/atelier';
import { AtelierCEUResponse } from '../_models/atelierCEUResponse';
import { DictionaryEntry } from '../_models/combustible';
import { Engin } from '../_models/engin';
import { Family } from '../_models/family';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { SnackbarService } from './snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class EnginAtelierService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {}

  get(atelierId: string) {
    return this.http.get<Atelier>(`${environment.apiUrl}/Engins/${atelierId}`, {
      headers: this.authService.headers
    });
  }

  getAtelier(atelierId: string, versionId?: string) {
    let params = new HttpParams();
    params = versionId ? params.set('versionId', versionId) : params;
    return this.http.get<Atelier>(`${environment.apiUrl}/Ateliers/${atelierId}`, {
      params: params,
      headers: this.authService.headers
    });
  }

  getAll(groupBy?: string, operationType?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;
    params = operationType ? params.set('operationType', operationType) : params;

    return this.http.get<Atelier[]>(`${environment.apiUrl}/Engins`, {
      params: params,
      headers: this.authService.headers
    });
  }

  getOnlyEngins(groupBy?: string, operationType?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;
    params = operationType ? params.set('operationType', operationType) : params;

    return this.http.get<Atelier[]>(`${environment.apiUrl}/Engins/onlyEngins`, {
      params: params,
      headers: this.authService.headers
    });
  }

  update(atelier: Atelier) {
    return this.http.put<Atelier>(`${environment.apiUrl}/Engins/${atelier.resourceId}`, atelier, { headers: this.authService.headers });
  }

  updateAtelier(atelier: Atelier) {
    return this.http.put<Atelier>(`${environment.apiUrl}/Ateliers/${atelier.resourceId}`, atelier, { headers: this.authService.headers });
  }

  add(atelier: Atelier) {
    return this.http.post<Atelier>(`${environment.apiUrl}/Engins`, atelier, {
      headers: this.authService.headers
    });
  }

  addAtelier(atelier: Atelier) {
    return this.http.post<Atelier>(`${environment.apiUrl}/Ateliers`, atelier, {
      headers: this.authService.headers
    });
  }

  delete(resourceId: string) {
    return this.http.delete(`${environment.apiUrl}/Engins/${resourceId}`, {
      headers: this.authService.headers
    });
  }

  deleteAtelier(resourceId: string) {
    return this.http.delete(`${environment.apiUrl}/Ateliers/${resourceId}`, {
      headers: this.authService.headers
    });
  }

  deleteMany(resourceIds: string[]) {
    return this.http.delete<{ results: { itemId?: string; resourceId?: string; id?: string; success: boolean }[] }>(`${environment.apiUrl}/Engins/deleteBulk`, {
      headers: this.authService.headers,
      body: { itemIds: resourceIds }
    });
  }

  duplicate(resourceId: string, code: string) {
    return this.http.post(`${environment.apiUrl}/Engins/duplicate?id=${resourceId}&code=${encodeURIComponent(code)}`, null, { headers: this.authService.headers });
  }

  duplicateAtelier(resourceId: string, code: string) {
    return this.http.post(`${environment.apiUrl}/Ateliers/duplicate?id=${resourceId}&code=${encodeURIComponent(code)}`, null, { headers: this.authService.headers });
  }

  getFamilies() {
    return this.http
      .get<Family[]>(`${environment.apiUrl}/Family/OwnFamilies?ressourceCode=${ResourceTypeCodes.Engin}`, { headers: this.authService.headers })
      .pipe(map((families) => families.map((f) => ({ familyId: f.familyId, familyCode: f.code, familyDisplayName: f.displayName }))));
  }

  getEnginesForAtelier(atelierId: string) {
    return this.http.get<Engin[]>(`${environment.apiUrl}/Engins/EnginesForAtelier/${atelierId}`, { headers: this.authService.headers });
  }

  computeCEU(atelier: Atelier, versionId: string) {
    let params = new HttpParams();
    if (versionId) params = params.set('versionId', versionId);
    return this.http.post<AtelierCEUResponse>(`${environment.apiUrl}/Ateliers/computeCEU`, atelier, {
      params: params,
      headers: this.authService.headers
    });
  }

  getExcelForCalculCeuEngins(fileName: string) {
    return this.http.get<Blob>(`${environment.apiUrl}/Engins/getExcelForCalculCeuEngins/${fileName}`, { headers: this.authService.headers, responseType: 'blob' as 'json' });
  }

  onClickNewItem(detailedResource: boolean) {
    const type = detailedResource ? 'atelier' : 'engin';
    this.router.navigate([`${SiteMapUrl.MenuEnginAtelier}/${type}/new`]);
  }

  getNavConfig({ dataSource, updateDataSource, navComponent }: NavListCommonProps) {
    return {
      header: {
        title: 'ressource_list_title_devices',
        actions: [
          { label: 'ressource_list_action_create_atelier', onClick: () => this.onClickNewItem(true) },
          { label: 'ressource_list_action_create_engin', onClick: () => this.onClickNewItem(false) }
        ],
        filters: [
          { label: 'ressource_list_filter_by_family', value: GroupBy.family },
          { label: 'ressource_list_filter_by_favorite', value: GroupBy.favorite }
        ],
        defaultFilter: GroupBy.family
      },
      getDataSource: (groupBy?: string, operationType?: string) => {
        let params = new HttpParams();
        params = groupBy ? params.set('groupBy', groupBy) : params;
        params = operationType ? params.set('operationType', operationType) : params;

        var newObservable = this.getAll(groupBy, operationType).pipe(
          map((o: any) => {
            o = o as DictionaryEntry<Atelier>[];
            if (o) {
              o.push({
                key: this.translate.instant('engin_category_maritime_dredge'),
                type: GlobalConstants.DREDGING_EQUIPMENT_TYPE,
                url: `${SiteMapUrl.MenuEnginAtelier}/${SiteMapUrl.MenuDredgingEquipment}`,
                value: []
              });
              o.push({
                key: this.translate.instant('common_field_tunneling'),
                type: GlobalConstants.TUNNELING_EQUIPMENT_TYPE,
                url: `${SiteMapUrl.MenuEnginAtelier}/${SiteMapUrl.MenuTunnelingEquipment}`,
                value: []
              });
              o = o.sort((a: any, b: any) => a.key.localeCompare(b.key));
              return o;
            } else {
              return [] as DictionaryEntry<Atelier>[];
            }
          })
        );
        return newObservable;
      },
      getItemConfig: (node: any) => {
        return {
          displayName: `${node.displayName} [${node.code}]`,
          url: `${SiteMapUrl.MenuEnginAtelier}/${node.detailedResource ? 'atelier' : 'engin'}/${node.resourceId}`,
          itemType: node.detailedResource ? 'atelier' : 'engin'
        };
      },
      onDelete: (selectedItems: any[]) => this.onDeleteEngins(selectedItems, dataSource, updateDataSource, navComponent),
      getEditableFamilyId: (node: any) => node.value?.[0]?.familyId,
      addFamilyType: ResourceTypeCodes.Engin
    };
  }

  private onDeleteEngins(selectedItems: any[], dataSource: MatTreeNestedDataSource<any>, updateDataSource: (newDataSource: any[]) => void, navComponent: any) {
    return navComponent.createDeleteHandler({
      deleteMany: (ids: string[]) => this.deleteMany(ids),
      getItemId: (item: any) => item?.resourceId || item?.id || item?.key,
      filterData: (data: any[], deletedIds: string[]) => this.filterNodes(data, deletedIds),
      successMessage: (count: number) => this.translate.instant(count > 1 ? 'success_resources_deleted' : 'success_resource_deleted'),
      errorMessage: 'error_no_item_selected',
      snackbarService: this.snackbarService
    })(selectedItems, dataSource, updateDataSource);
  }

  private filterNodes(nodes: any[], ids: string[]): any[] {
    if (!Array.isArray(nodes) || !nodes.length) {
      return [];
    }
    const filtered: any[] = [];
    nodes.forEach((node) => {
      if (!node) {
        return;
      }
      const nodeId = node?.resourceId || node?.id || node?.key;
      if (nodeId && ids.includes(nodeId)) {
        return;
      }
      if (Array.isArray(node.value)) {
        const nextChildren = this.filterNodes(node.value, ids);
        if (!nextChildren.length && !nodeId) {
          return;
        }
        filtered.push({ ...node, value: nextChildren });
        return;
      }
      filtered.push(node);
    });
    return filtered;
  }
}
